import { FC, useState, useEffect, useMemo, useRef } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
	Button,
	VStack,
	Text,
	Box,
	Flex,
	CloseButton,
	Skeleton,
	Spinner,
	useBreakpointValue,
	Input,
	InputGroup,
	InputLeftElement,
	List,
	ListItem,
	Checkbox,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import CustomModal from './CustomModal';
import {
	StringInputHook,
	TextareaInputHook,
	MultiSelectCreateableInputHook,
	MultiSelectValuesHook,
} from './form';
import {
	ICustomerProfile,
	ICustomerProfileForm,
	CustomerProfileFormSchema,
} from 'src/lib/schemas';
import {
	createCustomerProfile,
	updateCustomerProfile,
	getConditions,
} from 'src/services/account';
import { toastSuccess, toastError } from 'src/services/toast';
import { useContext } from 'react';
import { AppInputConfigContext } from 'src/contexts';
import useAccountConfigOptions from 'src/hooks/config/useAccountConfigOptions';
import { ProductFormModalProvider } from 'src/contexts/ProductFormModalContext';
import TwoLevelDropdown, {
	TwoLevelParentItem,
	TwoLevelChildItem,
} from 'src/components/common/TwoLevelDropdown';
import {
	IConditionGroup,
	IConditionValue,
} from 'src/lib/schemas/account/conditions';

interface CustomerProfileFormModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: (profile: ICustomerProfile) => void;
	editingProfile?: ICustomerProfile | null;
	initialProfile?: Partial<ICustomerProfile>;
}

const CustomerProfileFormModal: FC<CustomerProfileFormModalProps> = ({
	isOpen,
	onClose,
	onSuccess,
	editingProfile,
	initialProfile,
}) => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [conditions, setConditions] = useState<IConditionGroup[]>([]);
	const [isLoadingConditions, setIsLoadingConditions] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [isSearchFocused, setIsSearchFocused] = useState(false);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const searchResultsRef = useRef<HTMLDivElement>(null);
	const isEditing = !!editingProfile;

	// Get tones from context
	const { inputConfig, setInputConfig } = useContext(AppInputConfigContext);
	const tones = inputConfig?.Tone || [];

	// Hook for creating tones (like tags in ProductFormModal)
	const { createConfig: createTone } = useAccountConfigOptions('Tone');

	// Load conditions when modal opens
	useEffect(() => {
		if (isOpen && conditions.length === 0) {
			setIsLoadingConditions(true);
			getConditions()
				.then((data) => {
					setConditions(data);
				})
				.catch((error) => {
					console.error('Error loading conditions:', error);
					toastError('Failed to load conditions');
				})
				.finally(() => {
					setIsLoadingConditions(false);
				});
		}
	}, [isOpen, conditions.length]);

	// Load tones when modal opens (to ensure they're available for the form)
	const { fetchConfig: fetchTones } = useAccountConfigOptions('Tone');

	useEffect(() => {
		if (isOpen && tones.length === 0) {
			// Use the proper hook to fetch tones
			fetchTones()
				.then(() => {})
				.catch((error) => {
					console.error('🎵 Error fetching tones:', error);
				});
		}
	}, [isOpen, tones.length, fetchTones]);

	const formMethods = useForm<ICustomerProfileForm>({
		resolver: zodResolver(CustomerProfileFormSchema),
		defaultValues: {
			name: initialProfile?.name || '',
			description: initialProfile?.description || '',
			customer_keywords: [],
			customer_tones: [],
			customer_rules: [],
		},
	});

	// Helper to derive a unique condition key from any rule shape
	const extractConditionKey = (rule: any): string | null => {
		if (!rule) return null;
		if (typeof rule === 'string') return rule;

		const conditionId = rule.conditionId;
		const fileId = rule.file_id;

		if (!conditionId) return null;

		// Create unique key: conditionId|file_id (matches how conditions are indexed)
		return fileId ? `${conditionId}|${fileId}` : conditionId;
	};

	// Reset form when editingProfile changes and tones are loaded
	useEffect(() => {
		console.log('🔵🔵🔵 [CustomerProfileFormModal] useEffect triggered');
		console.log('🔵 [Modal] editingProfile:', editingProfile ? JSON.stringify({
			_id: editingProfile._id,
			name: editingProfile.name,
			customer_rules: editingProfile.customer_rules,
			customer_tones: editingProfile.customer_tones,
			hasRules: !!editingProfile.customer_rules?.length,
			hasTones: !!editingProfile.customer_tones?.length,
		}, null, 2) : 'null');
		console.log('🔵 [Modal] initialProfile:', initialProfile ? JSON.stringify({
			name: initialProfile.name,
			customer_rules: (initialProfile as any).customer_rules,
			customer_tones: (initialProfile as any).customer_tones,
		}, null, 2) : 'null');
		console.log('🔵 [Modal] isOpen:', isOpen);

		if (editingProfile) {
			// For editing profiles, reset immediately (don't wait for tones)
			const extractedRules = (editingProfile.customer_rules || [])
				.map(extractConditionKey)
				.filter(Boolean) as string[];
			console.log('🔵 [Modal] Extracted rules from editingProfile:', extractedRules);
			console.log('🔵 [Modal] Raw customer_rules:', editingProfile.customer_rules);
			console.log('🔵 [Modal] Raw customer_tones:', editingProfile.customer_tones);

			const resetData = {
				name: editingProfile.name,
				description: editingProfile.description,
				customer_keywords: editingProfile.customer_keywords || [],
				customer_tones: editingProfile.customer_tones || [],
				customer_rules: extractedRules,
			};
			console.log('🔵 [Modal] Resetting form with data:', JSON.stringify(resetData, null, 2));
			formMethods.reset(resetData);
		} else if (!editingProfile && initialProfile) {
			// For initial profiles, only reset if we have basic data
			const extractedKeys = (initialProfile?.customer_rules || [])
				.map(extractConditionKey)
				.filter(Boolean) as string[];

			const resetData = {
				name: initialProfile?.name || '',
				description: initialProfile?.description || '',
				customer_keywords: initialProfile?.customer_keywords || [],
				customer_tones: initialProfile?.customer_tones || [],
				customer_rules: extractedKeys,
			};
			formMethods.reset(resetData);
		} else if (!editingProfile && !initialProfile) {
			// For new profiles, reset to empty values
			const resetData = {
				name: '',
				description: '',
				customer_keywords: [],
				customer_tones: [],
				customer_rules: [],
			};
			formMethods.reset(resetData);
		}
	}, [editingProfile, initialProfile, formMethods, isOpen]);

	// Re-reset form when tones are loaded to show preselected tones
	useEffect(() => {
		if (editingProfile && tones.length > 0) {
			formMethods.reset({
				name: editingProfile.name,
				description: editingProfile.description,
				customer_keywords: editingProfile.customer_keywords || [],
				customer_tones: editingProfile.customer_tones || [],
				customer_rules: (editingProfile.customer_rules || [])
					.map(extractConditionKey)
					.filter(Boolean) as string[],
			});
		}
	}, [tones.length, editingProfile, formMethods]);

	// When conditions load, map editingProfile.customer_rules to correct condition IDs
	useEffect(() => {
		if (!editingProfile || conditions.length === 0) {
			return;
		}

		const sourceRules = editingProfile.customer_rules || [];

		if (sourceRules.length > 0) {
			const allAvailableKeys: string[] = [];

			// Build mapping table for conditions
			for (const group of conditions) {
				for (const v of group.values || []) {
					const uniqueKey = group.file_id
						? `${v.value.id}|${group.file_id}`
						: v.value.id;

					allAvailableKeys.push(uniqueKey);
				}
			}

			const mappedKeys: string[] = [];
			for (const rule of sourceRules as any[]) {
				const uniqueKey = extractConditionKey(rule);
				if (uniqueKey && allAvailableKeys.includes(uniqueKey)) {
					mappedKeys.push(uniqueKey);
				}
			}

			if (mappedKeys.length > 0) {
				formMethods.setValue('customer_rules', mappedKeys, {
					shouldDirty: false,
					shouldValidate: false,
				});
			}
		}
	}, [conditions, editingProfile, formMethods]);

	const { handleSubmit, reset } = formMethods;

	// Flatten for chips/lookup (current API model only)
	const flattenedConditions = useMemo<IConditionValue[]>(() => {
		if (!conditions) return [] as IConditionValue[];
		return conditions.flatMap((group) => group.values || []);
	}, [conditions]);

	// Map conditions into two-level dropdown structure
	const conditionGroups: TwoLevelParentItem[] = useMemo(() => {
		if (!conditions || conditions.length === 0) return [];
		return conditions.map((group) => ({
			label: group.name || 'Other',
			children: (group.values || []).map((v) => ({
				label: v.value.description,
				// Use unique key as value for precise selection detection
				value: group.file_id ? `${v.value.id}|${group.file_id}` : v.value.id,
			})),
		}));
	}, [conditions]);

	// Filtered search results based on search query
	const filteredSearchResults = useMemo(() => {
		if (!searchQuery.trim() || !conditions || conditions.length === 0)
			return [];

		const query = searchQuery.toLowerCase().trim();
		
		// Need at least 2 characters to search
		if (query.length < 2) return [];
		
		const results: Array<{
			groupName: string;
			groupId: string;
			fileId: string;
			label: string;
			value: string;
		}> = [];

		// Track seen labels to avoid duplicates
		const seenLabels = new Set<string>();

		for (const group of conditions) {
			const groupName = group.name || 'Other';

			for (const v of group.values || []) {
				const itemLabel = v.value.description || '';
				const uniqueKey = group.file_id
					? `${v.value.id}|${group.file_id}`
					: v.value.id;

				// Only include if item label contains the search query
				// Use a unique key based on label (lowercase) to avoid duplicates across groups
				const labelKey = itemLabel.toLowerCase();
				
				if (
					labelKey.includes(query) &&
					!seenLabels.has(labelKey)
				) {
					seenLabels.add(labelKey);
					results.push({
						groupName,
						groupId: group.id,
						fileId: group.file_id || '',
						label: itemLabel,
						value: uniqueKey,
					});
				}
			}
		}

		// Sort by relevance (exact matches first, then starts with, then contains)
		return results
			.sort((a, b) => {
				const aLower = a.label.toLowerCase();
				const bLower = b.label.toLowerCase();
				const aExact = aLower === query;
				const bExact = bLower === query;
				const aStartsWith = aLower.startsWith(query);
				const bStartsWith = bLower.startsWith(query);

				if (aExact && !bExact) return -1;
				if (bExact && !aExact) return 1;
				if (aStartsWith && !bStartsWith) return -1;
				if (bStartsWith && !aStartsWith) return 1;
				return aLower.localeCompare(bLower);
			})
			.slice(0, 50); // Limit to 50 results for performance
	}, [searchQuery, conditions]);

	// Handle click outside search results to close dropdown
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			const target = event.target as Node;
			const isInsideSearch = searchInputRef.current?.contains(target);
			const isInsideResults = searchResultsRef.current?.contains(target);

			if (!isInsideSearch && !isInsideResults) {
				setIsSearchFocused(false);
			}
		};

		if (isSearchFocused) {
			document.addEventListener('mousedown', handleClickOutside);
		}
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isSearchFocused]);

	// Reset search when modal closes
	useEffect(() => {
		if (!isOpen) {
			setSearchQuery('');
			setIsSearchFocused(false);
		}
	}, [isOpen]);

	// Handle search result selection
	const handleSearchResultSelect = (uniqueKey: string) => {
		const current = formMethods.getValues('customer_rules') || [];

		if (current.includes(uniqueKey)) {
			// Remove if already selected
			formMethods.setValue(
				'customer_rules',
				current.filter((ruleKey) => ruleKey !== uniqueKey),
				{
					shouldDirty: true,
					shouldValidate: true,
				},
			);
		} else {
			// Add if not selected
			formMethods.setValue('customer_rules', [...current, uniqueKey], {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	};

	// Selected groups view helpers
	const selectedRuleIds = formMethods.watch('customer_rules') ?? [];

	// Since dropdown now uses unique keys as values, we can pass selectedRuleIds directly

	const groupedSelectedConditions = useMemo(
		() =>
			conditions
				.map((group) => {
					const selectedValues = (group.values || []).filter((v) => {
						// Use group.file_id as the category identifier
						const uniqueKey = group.file_id
							? `${v.value.id}|${group.file_id}`
							: v.value.id;
						return selectedRuleIds.includes(uniqueKey);
					});
					return selectedValues.length
						? { id: group.id, name: group.name, values: selectedValues }
						: null;
				})
				.filter(Boolean) as Array<{
				id: string;
				name: string;
				values: {
					value: { id: string; description: string; file_id?: string };
				}[];
			}>,
		[conditions, selectedRuleIds],
	);

	const removeConditionByKey = (uniqueKey: string) => {
		const current = formMethods.getValues('customer_rules') || [];
		formMethods.setValue(
			'customer_rules',
			current.filter((v: string) => v !== uniqueKey),
			{ shouldDirty: true, shouldValidate: true },
		);
	};

	const removeGroupById = (groupId: string) => {
		const group = conditions.find((g) => g.id === groupId);
		if (!group) return;

		// Create set of unique keys to remove for this group
		const keysToRemove = new Set(
			(group.values || []).map((v) => {
				// Use group.file_id as the category identifier
				const fileId = group.file_id;
				return fileId ? `${v.value.id}|${fileId}` : v.value.id;
			}),
		);

		const current = formMethods.getValues('customer_rules') || [];
		formMethods.setValue(
			'customer_rules',
			current.filter((uniqueKey: string) => !keysToRemove.has(uniqueKey)),
			{ shouldDirty: true, shouldValidate: true },
		);
	};

	// Build a lookup map using unique keys (conditionId|file_id) -> details payload for API
	const conditionDetailsByKey = useMemo(() => {
		const map: Record<
			string,
			{
				conditionId: string;
				description: string;
				facebookId: string | null;
				googleId: string | null;
				file_id: string;
			}
		> = {};
		for (const group of conditions) {
			const fbMap = group.facebookMapping?.values || [];
			const ggMap = group.googleMapping?.values || [];
			for (const v of group.values || []) {
				const id = v.value.id;
				const description = v.value.description;
				const fb = fbMap.find((m) => m.conditionId === id);
				const gg = ggMap.find((m) => m.conditionId === id);

				// Use group.file_id as the category identifier, not individual mapping file
				const categoryFileId = group.file_id || fb?.file || gg?.file || '';

				// Create unique key: conditionId|category_file_id
				const uniqueKey = categoryFileId ? `${id}|${categoryFileId}` : id;

				map[uniqueKey] = {
					conditionId: id,
					description,
					facebookId: fb?.id ?? null,
					googleId: gg?.id ?? null,
					file_id: categoryFileId,
				};
			}
		}
		return map;
	}, [conditions]);

	const handleToggleCondition = (
		item: TwoLevelChildItem | TwoLevelParentItem,
	) => {
		const uniqueKey =
			(item as TwoLevelChildItem).value || (item as TwoLevelParentItem).value;
		if (!uniqueKey) return;

		const current = formMethods.getValues('customer_rules') || [];

		if (current.includes(uniqueKey)) {
			// Remove if already selected
			formMethods.setValue(
				'customer_rules',
				current.filter((ruleKey) => ruleKey !== uniqueKey),
				{
					shouldDirty: true,
					shouldValidate: true,
				},
			);
		} else {
			// Add if not selected
			formMethods.setValue('customer_rules', [...current, uniqueKey], {
				shouldDirty: true,
				shouldValidate: true,
			});
		}
	};

	const onSubmit = async (data: ICustomerProfileForm) => {
		setIsSubmitting(true);
		try {
			let savedProfile: ICustomerProfile;

			const profilePayload = {
				name: data.name,
				description: data.description,
				customer_tones: data.customer_tones || [],
				customer_rules: data.customer_rules.map((uniqueKey) => {
					// Split the unique key to get conditionId and file_id
					const parts = uniqueKey.split('|');
					return {
						conditionId: parts[0],
						file_id: parts[1] || '',
					};
				}),
			} as any;

			if (isEditing && editingProfile) {
				// Update existing profile
				savedProfile = await updateCustomerProfile(
					editingProfile._id,
					profilePayload,
				);
				// Ensure the returned profile has the correct _id
				savedProfile._id = editingProfile._id;
				toastSuccess('Customer profile updated successfully');
			} else {
				// Create new profile
				savedProfile = await createCustomerProfile(profilePayload);
				toastSuccess('Customer profile created successfully');
			}

			// Enrich saved profile with full customer_rules details using current conditions
			const selectedIds = data.customer_rules || [];
			const enrichedRules = (selectedIds || []).map((uniqueKey) => {
				const details = (conditionDetailsByKey as any)[uniqueKey];
				return (
					details || {
						conditionId: uniqueKey.split('|')[0] || uniqueKey, // Extract conditionId from unique key
						description: '',
						facebookId: null,
						googleId: null,
						file_id: '',
					}
				);
			});
			const enrichedProfile = {
				...savedProfile,
				customer_rules: enrichedRules,
			} as ICustomerProfile;

			onSuccess(enrichedProfile);
		} catch (error) {
			console.error('Error saving customer profile:', error);
			toastError(
				isEditing
					? 'Failed to update customer profile'
					: 'Failed to create customer profile',
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleClose = () => {
		reset();
		onClose();
	};

	// Handle creating new tone (like tags in ProductFormModal)
	const handleCreateToneOption = async (name: string) => {
		if (!name.trim()) return;

		try {
			await createTone({ name });
			// The tone will be automatically added to the context
		} catch (error: any) {
			toastError(error);
		}
	};

	// Responsive values for better mobile experience
	const stackSpacing = useBreakpointValue({ base: 4, md: 6 });
	const buttonSpacing = useBreakpointValue({ base: 2, md: 3 });
	const footerDirection = useBreakpointValue<'row' | 'column'>({
		base: 'column',
		sm: 'row',
	});

	const footerContent = (
		<Flex
			direction={footerDirection}
			gap={buttonSpacing}
			width="100%"
			align={footerDirection === 'column' ? 'stretch' : 'center'}
			justify={footerDirection === 'column' ? 'stretch' : 'flex-end'}
		>
			<Button
				variant="ghost"
				onClick={handleClose}
				isDisabled={isSubmitting}
				order={footerDirection === 'column' ? 2 : 1}
			>
				Cancel
			</Button>
			<Button
				variant="orangeSolid"
				onClick={handleSubmit(onSubmit)}
				isLoading={isSubmitting}
				loadingText={isEditing ? 'Updating...' : 'Creating...'}
				order={footerDirection === 'column' ? 1 : 2}
			>
				Save
			</Button>
		</Flex>
	);

	return (
		<CustomModal
			isOpen={isOpen}
			onClose={handleClose}
			size="3xl"
			header={
				isEditing ? 'Edit Customer Profile' : 'Create New Customer Profile'
			}
			footer={footerContent}
		>
			<FormProvider {...formMethods}>
				<ProductFormModalProvider isProductFormModal={true}>
					<form onSubmit={handleSubmit(onSubmit)}>
						<VStack spacing={stackSpacing} align="stretch">
							<Box>
								<Text fontSize="sm" color="gray.600" mb={4}>
									Create a customer profile that describes your target audience
									and communication preferences.
								</Text>
							</Box>

							<StringInputHook
								name="name"
								label="Profile Name"
								placeholder="e.g., Luxury Executives, Young Professionals"
								required
								formLabelProps={{ fontSize: '14px', fontWeight: 'semibold' }}
							/>

							<TextareaInputHook
								name="description"
								label="Description"
								placeholder="Describe your target customer segment, their characteristics, preferences, and behaviors..."
								required
								formLabelProps={{ fontSize: '14px', fontWeight: 'semibold' }}
							/>

							<Box>
								<Flex direction="column" gap={1}>
									<Text fontSize="14px" fontWeight="semibold">
										Audience rules{' '}
										<Text as="span" color="red.500">
											*
										</Text>
									</Text>
									<Text as="span" color="#718096" fontSize="smaller" mb={2}>
										Please select to accurately describe your target customer
									</Text>

									{/* Search field for audience rules */}
									<Box position="relative" mb={2}>
										<InputGroup>
											<InputLeftElement pointerEvents="none">
												<SearchIcon color="gray.400" />
											</InputLeftElement>
											<Input
												ref={searchInputRef}
												placeholder="Search audience rules..."
												value={searchQuery}
												onChange={(e) => setSearchQuery(e.target.value)}
												onFocus={() => setIsSearchFocused(true)}
												borderRadius="5px"
												borderColor="#E2E8F0"
												_hover={{ borderColor: '#CBD5E0' }}
												_focus={{
													borderColor: '#2C6ECB',
													boxShadow: '0 0 0 1px #2C6ECB',
												}}
												isDisabled={isLoadingConditions}
											/>
										</InputGroup>

										{/* Search results dropdown */}
										{isSearchFocused &&
											searchQuery.trim() &&
											filteredSearchResults.length > 0 && (
												<Box
													ref={searchResultsRef}
													position="absolute"
													top="100%"
													left={0}
													right={0}
													mt={1}
													bg="white"
													borderRadius="5px"
													boxShadow="0 4px 11px rgba(0,0,0,.1)"
													border="1px solid"
													borderColor="#E2E8F0"
													maxH="250px"
													overflowY="auto"
													zIndex={10200}
												>
													<List spacing={0}>
														{filteredSearchResults.map((result) => {
															const isSelected = selectedRuleIds.includes(
																result.value,
															);
															return (
																<ListItem
																	key={result.value}
																	px={3}
																	py={2}
																	cursor="pointer"
																	_hover={{ bg: '#f9f9fa' }}
																	borderBottom="1px solid"
																	borderColor="#E2E8F0"
																	onClick={() =>
																		handleSearchResultSelect(result.value)
																	}
																>
																	<Flex align="center" gap={2}>
																		<Checkbox
																			isChecked={isSelected}
																			size="sm"
																			pointerEvents="none"
																			sx={{
																				'& .chakra-checkbox__control[data-checked]':
																					{
																						backgroundColor: 'orange.base',
																						borderColor: 'orange.base',
																						color: 'white',
																					},
																				'& .chakra-checkbox__control:not([data-checked])':
																					{
																						borderColor: 'gray.300',
																					},
																			}}
																		/>
																		<Box>
																			<Text fontSize="14px">
																				{result.label}
																			</Text>
																			<Text
																				fontSize="12px"
																				color="gray.500"
																			>
																				{result.groupName}
																			</Text>
																		</Box>
																	</Flex>
																</ListItem>
															);
														})}
													</List>
												</Box>
											)}

										{/* No results message */}
										{isSearchFocused &&
											searchQuery.trim() &&
											filteredSearchResults.length === 0 &&
											!isLoadingConditions && (
												<Box
													ref={searchResultsRef}
													position="absolute"
													top="100%"
													left={0}
													right={0}
													mt={1}
													bg="white"
													borderRadius="5px"
													boxShadow="0 4px 11px rgba(0,0,0,.1)"
													border="1px solid"
													borderColor="#E2E8F0"
													p={3}
													zIndex={10200}
												>
													<Text fontSize="14px" color="gray.500">
														No audience rules found for "{searchQuery}"
													</Text>
												</Box>
											)}
									</Box>

									{/* Always show dropdown, but disable when loading */}
									{isLoadingConditions ? (
										<Button
											w="fit-content"
											variant="outline"
											justifyContent="flex-start"
											isDisabled={true}
											rightIcon={<Spinner size="sm" />}
										>
											Add audience rule
										</Button>
									) : (
										<TwoLevelDropdown
											items={conditionGroups}
											onSelect={handleToggleCondition}
											buttonLabel="Add audience rule"
											selectedValues={selectedRuleIds}
											multiSelect={true}
										/>
									)}
								</Flex>
								{/* Selected conditions grouped by first-level title */}
								<VStack spacing={3} align="stretch" mt={4}>
									{isLoadingConditions
										? // Show skeleton for selected conditions while loading
											[1, 2].map((index) => (
												<Flex
													key={index}
													align="center"
													gap={3}
													bg="#F8F8F8"
													borderRadius="lg"
													px={3}
													py={2}
												>
													<Skeleton height="20px" width="100px" />
													<Flex gap={2} flex="1">
														<Skeleton
															height="24px"
															width="80px"
															borderRadius="md"
														/>
														<Skeleton
															height="24px"
															width="100px"
															borderRadius="md"
														/>
														<Skeleton
															height="24px"
															width="90px"
															borderRadius="md"
														/>
													</Flex>
													<Skeleton
														height="20px"
														width="20px"
														borderRadius="md"
													/>
												</Flex>
											))
										: // Show actual selected conditions when loaded
											groupedSelectedConditions.map((group) => (
												<Flex
													key={group.id}
													align="center"
													gap={3}
													bg="#F8F8F8"
													borderRadius="lg"
													px={3}
													py={2}
													wrap="wrap"
												>
													<Text fontWeight="bold" minW="fit-content">
														{group.name}
													</Text>
													<Flex gap={2} wrap="wrap" flex="1">
														{group.values.map((v) => (
															<Flex
																key={v.value.id}
																align="center"
																gap={2}
																px={2}
																py={1}
																borderRadius="md"
																bg="white"
																border="1px solid"
																borderColor="#E2E8F0"
															>
																<Text fontSize="14px">
																	{v.value.description}
																</Text>
																<CloseButton
																	size="sm"
																	onClick={() =>
																		removeConditionByKey(
																			// Find the group to get correct file_id
																			(() => {
																				const targetGroup =
																					groupedSelectedConditions.find(
																						(group) =>
																							group.values.some(
																								(val) =>
																									val.value.id === v.value.id,
																							),
																					);
																				const fileId = targetGroup
																					? conditions.find(
																							(c) => c.id === targetGroup.id,
																						)?.file_id
																					: undefined;
																				return fileId
																					? `${v.value.id}|${fileId}`
																					: v.value.id;
																			})(),
																		)
																	}
																/>
															</Flex>
														))}
													</Flex>
													<CloseButton
														aria-label="Remove group"
														size="sm"
														onClick={() => removeGroupById(group.id)}
													/>
												</Flex>
											))}
								</VStack>

								{/* Error message for conditions */}
								{formMethods.formState.errors.customer_rules && (
									<Text color="red.500" fontSize="sm" mt={1}>
										{formMethods.formState.errors.customer_rules.message}
									</Text>
								)}
							</Box>

							<MultiSelectCreateableInputHook
								name="customer_tones"
								label="Tones"
								placeholder="Select/search"
								options={inputConfig['Tone'] ?? []}
								isLoading={false}
								onCreateOption={handleCreateToneOption}
								isMulti
								shouldShowSkeleton={false}
								required
								formLabelProps={{ fontSize: '14px', fontWeight: 'semibold' }}
							/>
							<MultiSelectValuesHook
								name="customer_tones"
								title="Tones"
								options={inputConfig['Tone'] ?? []}
							/>
							{/* Extra space to allow dropdowns to expand without being clipped */}
							<Box h="140px" />
						</VStack>
					</form>
				</ProductFormModalProvider>
			</FormProvider>
		</CustomModal>
	);
};

export default CustomerProfileFormModal;
